import { useForm } from 'react-hook-form';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import Heading from 'storybookConfig/stories/molecules/Heading';
import Input from 'storybookConfig/stories/molecules/Input';
import Form from 'storybookConfig/stories/organisms/Form';
import { useAppSelector, useAppDispatch } from 'store/index';
import { selectCompanyId } from 'store/selectors/me/company';
import { updateMarketplaceOnboarding } from 'store/slices/me';
import { updateCompany } from 'utils/api/companies';
import type { CompanyMarketplace } from 'types/models/company';
import { useMutation } from '@tanstack/react-query';
import useAlertQueue from 'hooks/useAlertQueue';
import TertiaryButton from 'storybookConfig/stories/molecules/Button/TertiaryButton';
import Flex from '@react-css/flex';
import { URL_FIELD_REGEX, normalizeURL } from 'utils/strings';
import type { FormProps } from './types';
import useSegment from 'hooks/useSegment';

const socials = [
  {
    key: 'websiteURL',
    label: 'Website',
  },
  {
    key: 'instagramURL',
    label: 'Instagram',
  },
  {
    key: 'facebookURL',
    label: 'Facebook',
  },
  {
    key: 'twitterURL',
    label: 'Twitter',
  },
  {
    key: 'linkedInURL',
    label: 'LinkedIn',
  },
  {
    key: 'tikTokURL',
    label: 'TikTok',
  },
];

// URL validation function using the existing regex for consistency
const validateURL = (value: string) => {
  if (!value || value.trim() === '') return true; // Optional field

  const trimmedUrl = value.trim();

  // Remove protocol for regex validation if present
  const urlWithoutProtocol = trimmedUrl.replace(/^https?:\/\//, '');

  if (!URL_FIELD_REGEX.test(urlWithoutProtocol)) {
    return 'Please enter a valid URL';
  }

  return true;
};

const BrandSocials = ({ onNext, onBack, marketplaceData, setMarketplaceData }: FormProps) => {
  const companyId = useAppSelector(selectCompanyId);
  const dispatch = useAppDispatch();
  const { addErrorAlert, addSuccessAlert } = useAlertQueue();

  type FormFields = Pick<
    CompanyMarketplace,
    'websiteURL' | 'instagramURL' | 'facebookURL' | 'twitterURL' | 'linkedInURL' | 'tikTokURL'
  >;

  const { handleSubmit, register, formState } = useForm<FormFields>({
    mode: 'onChange',
    values: {
      websiteURL: marketplaceData?.websiteURL || '',
      instagramURL: marketplaceData?.instagramURL || '',
      facebookURL: marketplaceData?.facebookURL || '',
      twitterURL: marketplaceData?.twitterURL || '',
      linkedInURL: marketplaceData?.linkedInURL || '',
      tikTokURL: marketplaceData?.tikTokURL || '',
    },
  });
  const analytics = useSegment();

  const updatingCompany = useMutation({
    mutationFn: (data: Partial<CompanyMarketplace>) => {
      // Normalize URLs before saving
      const normalizedData = {
        ...data,
        websiteURL: data.websiteURL ? normalizeURL(data.websiteURL) : data.websiteURL,
        instagramURL: data.instagramURL ? normalizeURL(data.instagramURL) : data.instagramURL,
        facebookURL: data.facebookURL ? normalizeURL(data.facebookURL) : data.facebookURL,
        twitterURL: data.twitterURL ? normalizeURL(data.twitterURL) : data.twitterURL,
        linkedInURL: data.linkedInURL ? normalizeURL(data.linkedInURL) : data.linkedInURL,
        tikTokURL: data.tikTokURL ? normalizeURL(data.tikTokURL) : data.tikTokURL,
      };

      setMarketplaceData(normalizedData);

      const updatedData = {
        websiteURL: normalizedData.websiteURL,
        instagramURL: normalizedData.instagramURL,
        facebookURL: normalizedData.facebookURL,
        twitterURL: normalizedData.twitterURL,
        linkedInURL: normalizedData.linkedInURL,
        tikTokURL: normalizedData.tikTokURL,
      };
      return updateCompany(companyId ?? '', {
        marketplaceSettings: updatedData,
      });
    },
    onSuccess: (data) => {
      analytics?.track('Supplier Onboarding Social Profiles Completed', {
        website: data.marketplaceSettings.websiteURL,
        instagram: data.marketplaceSettings.instagramURL,
        facebook: data.marketplaceSettings.facebookURL,
        twitter: data.marketplaceSettings.twitterURL,
        linkedIn: data.marketplaceSettings.linkedInURL,
        tikTok: data.marketplaceSettings.tikTokURL,
      });
      dispatch(updateMarketplaceOnboarding(data.marketplaceSettings));
      addSuccessAlert('Added Contact Info', 'Your contact has been saved.');
      if (onNext) {
        onNext();
      }
    },
    onError: (error: { message: string }) => {
      analytics?.track('Supplier Onboarding Step Error', {
        step_name: 'socials',
        error_message: error.message,
      });
      addErrorAlert('Something went wrong', error.message);
      console.error('Unable to update marketplace settings', error);
    },
  });

  return (
    <Form onSubmit={handleSubmit((data) => updatingCompany.mutate(data))}>
      <Flex>
        <TertiaryButton
          type="button"
          $removePadding="left"
          onClick={onBack}
          $iconName="navigate_before"
        >
          Back
        </TertiaryButton>
      </Flex>
      <Heading variant="Headings/H3">Social Profiles</Heading>

      <Form.FieldGroup>
        {socials.map((social) => (
          <Form.Field
            key={social.key}
            labelText={social.label}
            id={social.key}
            errorText={formState.errors[social.key as keyof FormFields]?.message}
          >
            <Input
              {...register(social.key as keyof FormFields, {
                validate: validateURL,
                onChange: (e) => {
                  const { value } = e.target;
                  setMarketplaceData({
                    [social.key]: value,
                  });
                },
              })}
              placeholder={`https://www.${social.label.toLowerCase()}.com/yourbrand`}
              isFullWidth
            />
          </Form.Field>
        ))}
      </Form.FieldGroup>
      <Form.Footer column gap="16px">
        <PrimaryButton type="submit" disabled={updatingCompany.status === 'loading'}>
          {updatingCompany.status === 'loading' ? 'Saving...' : 'Next'}
        </PrimaryButton>
      </Form.Footer>
    </Form>
  );
};

export default BrandSocials;
